import { useEffect, useState } from 'react';
import {
  EXTENSION_PRESENCE_ATTR,
  EXTENSION_PROBE_IDS,
  detectBrowser,
  extensionProbeUrl,
} from '@heybesafe/shared';

/**
 * True if the browser extension has tagged this page as installed. The
 * extension's content script sets {@link EXTENSION_PRESENCE_ATTR} on `<html>`
 * when it loads on a Hey Be Safe origin.
 */
export function isExtensionDetected(): boolean {
  return document.documentElement.hasAttribute(EXTENSION_PRESENCE_ATTR);
}

/**
 * Second detection channel: fetch a resource the extension publishes to our
 * origin only (`web_accessible_resources`, see shared `extensionPresence.ts`).
 *
 * This exists because the presence attribute has a blind spot that no amount
 * of re-reading the DOM can fix: Chrome does not inject content scripts into
 * tabs that were already open when an extension was installed. Someone who
 * follows the "install the extension" link, installs, and comes back to their
 * safe therefore keeps a page whose `<html>` will never be tagged until they
 * reload. Fetching an extension URL is handled by the browser rather than by
 * an injected script, so it starts succeeding the moment the extension exists.
 *
 * Resolves false on any failure — no extension, wrong browser (Firefox has no
 * fixed `moz-extension://` origin to probe), or a blocked request.
 */
export async function probeExtension(): Promise<boolean> {
  // `chrome-extension://` is a Chromium scheme. On Firefox and Safari the
  // fetch can only fail, and would log a console error on every attempt.
  const browser = detectBrowser();
  if (browser === 'firefox' || browser === 'safari') return false;

  const results = await Promise.all(
    EXTENSION_PROBE_IDS.map(async (id) => {
      try {
        const res = await fetch(extensionProbeUrl(id), { method: 'GET' });
        return res.ok;
      } catch {
        return false;
      }
    }),
  );
  return results.some(Boolean);
}

/**
 * Reactively detects the browser extension through both channels.
 *
 * The attribute is read once and then watched with a MutationObserver (the
 * content script sets it at `document_idle`, which can land after React has
 * mounted). The probe covers the mid-session install the attribute cannot see,
 * and re-runs whenever this hook mounts — which includes unlocking the safe,
 * since the checklist mounts then — and whenever the tab is shown or focused
 * again, which is how the user returns from the extension store.
 *
 * Detection is sticky: once seen we stop looking (an extension uninstall
 * mid-session shouldn't un-complete the onboarding step).
 */
export function useExtensionDetected(): boolean {
  const [detected, setDetected] = useState(isExtensionDetected);

  useEffect(() => {
    if (detected) return;
    // Cover the race where the attribute appeared between the initial render
    // and this effect running.
    if (isExtensionDetected()) {
      setDetected(true);
      return;
    }

    let cancelled = false;
    const settle = (found: boolean) => {
      if (found && !cancelled) setDetected(true);
    };
    const runProbe = () => { void probeExtension().then(settle); };

    const observer = new MutationObserver(() => {
      if (isExtensionDetected()) settle(true);
    });
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: [EXTENSION_PRESENCE_ATTR],
    });

    // Returning from the store tab fires visibilitychange; focus covers the
    // separate-window case, where the safe's tab was never hidden.
    const onWake = () => { if (document.visibilityState === 'visible') runProbe(); };
    document.addEventListener('visibilitychange', onWake);
    window.addEventListener('focus', runProbe);
    runProbe();

    return () => {
      cancelled = true;
      observer.disconnect();
      document.removeEventListener('visibilitychange', onWake);
      window.removeEventListener('focus', runProbe);
    };
  }, [detected]);

  return detected;
}
